import { useEffect, useState } from 'react'
import { Skeleton } from 'components/UnvUI'
import { TextField, MenuItem, Button } from '@mui/material'
import { useTranslation } from 'react-i18next'

import $api from 'http/index'
import Notification from 'utils/Notifications'

import styles from './AbsenceJustificationPage.module.scss'

const fmtDate = (iso) => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('ru-RU')
  } catch {
    return '—'
  }
}

const AbsenceJustificationPage = () => {
  const { t } = useTranslation('common')
  const STATUS_LABELS = {
    pending: { label: t('journal.absence_status_pending'), cls: 'unv-badge--warning' },
    approved: { label: t('journal.absence_status_approved'), cls: 'unv-badge--success' },
    rejected: { label: t('journal.absence_status_rejected'), cls: 'unv-badge--danger' },
  }
  const [list, setList] = useState([])
  const [subjects, setSubjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [form, setForm] = useState({ date: '', subject: '', reason: '', file: null })
  const [submitting, setSubmitting] = useState(false)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const load = async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await $api.get('/academic/absence-justifications/', { params: { mine: true } })
      setList(res.data?.results || res.data || [])
    } catch {
      setError(true)
      setList([])
    } finally {
      setLoading(false)
    }
  }

  const loadSubjects = async () => {
    try {
      const res = await $api.get('/academic/subjects/')
      setSubjects(res.data?.results || res.data || [])
    } catch {
      setSubjects([])
    }
  }

  useEffect(() => {
    load()
    loadSubjects()
  }, [])

  const submit = async (e) => {
    e.preventDefault()
    if (!form.date || !form.reason) {
      setNotify({ isOpen: true, message: t('journal.fill_date_reason'), type: 'warning', sound: 'warning' })
      return
    }
    setSubmitting(true)
    try {
      const fd = new FormData()
      fd.append('missed_at', form.date)
      if (form.subject) fd.append('subject', form.subject)
      fd.append('reason', form.reason)
      if (form.file) fd.append('file', form.file)
      await $api.post('/academic/absence-justifications/', fd)
      setNotify({ isOpen: true, message: t('schedule.request_sent'), type: 'success', sound: 'success' })
      setForm({ date: '', subject: '', reason: '', file: null })
      load()
    } catch {
      setNotify({ isOpen: true, message: t('assignments.send_error'), type: 'error', sound: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={`unv-card ${styles.header}`}>
        <h1 className={styles.title}>{t('journal.absence_justification_title')}</h1>
      </div>

      <form className={`unv-card ${styles.formCard}`} onSubmit={submit}>
        <h3>{t('schedule.new_request')}</h3>
        <div className={styles.formGrid}>
          <TextField
            type="date"
            label={t('journal.absence_date')}
            InputLabelProps={{ shrink: true }}
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
          />
          <TextField
            select
            label={t('schedule.subject')}
            value={form.subject}
            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
          >
            <MenuItem value="">{t('schedule.select_lesson')}</MenuItem>
            {subjects.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('schedule.reason_col')}
            multiline
            rows={3}
            value={form.reason}
            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            className={styles.fullWidth}
          />
          <div className={styles.fileField}>
            <label>{t('journal.certificate_or_doc')}</label>
            <input
              type="file"
              onChange={(e) => setForm((f) => ({ ...f, file: e.target.files?.[0] || null }))}
            />
          </div>
        </div>
        <div className={styles.actions}>
          <Button type="submit" variant="contained" disabled={submitting}>
            {t('common.submit')}
          </Button>
        </div>
      </form>

      <div className={`unv-card ${styles.content}`}>
        <h3>{t('journal.my_requests')}</h3>
        {loading ? (
          <div className={styles.loaderWrap}>
            <Skeleton variant="table" rows={5} cols={4} />
          </div>
        ) : error ? (
          <div className="unv-empty">{t('schedule.section_in_dev')}</div>
        ) : list.length === 0 ? (
          <div className="unv-empty">{t('journal.no_requests')}</div>
        ) : (
          <table className="unv-table">
            <thead>
              <tr>
                <th>{t('journal.absence_date')}</th>
                <th>{t('schedule.subject')}</th>
                <th>{t('schedule.reason_col')}</th>
                <th>{t('journal.submitted_at')}</th>
                <th>{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => {
                const s = STATUS_LABELS[r.status] || { label: r.status, cls: '' }
                return (
                  <tr key={r.id}>
                    <td>{fmtDate(r.missed_at)}</td>
                    <td>{r.subject_title || r.subject_name || '—'}</td>
                    <td>{r.reason || '—'}</td>
                    <td>{fmtDate(r.created_at)}</td>
                    <td>
                      <span className={`unv-badge ${s.cls}`}>{s.label}</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default AbsenceJustificationPage
